import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Users, UserPlus, Mail, Send, Upload, Search, Filter, Check, X,
  Clock, Eye, UserCheck, MoreHorizontal, FileSpreadsheet, RefreshCw
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PreRegistrationInvites = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [selectedInvites, setSelectedInvites] = useState([]);

  const getToken = () => {
    try {
      const s = localStorage.getItem('eventflow-auth');
      if (s) {
        const p = JSON.parse(s);
        if (p?.state?.token) return p.state.token;
      }
    } catch { /* noop */ }
    return localStorage.getItem('eventflow_token') || '';
  };
  const token = getToken();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch invites
  const { data: invites = [], isLoading } = useQuery({
    queryKey: ['event-invites', eventId, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      const res = await fetch(`${API_URL}/api/events/${eventId}/invites?${params}`, { headers });
      if (!res.ok) throw new Error('Failed to fetch invites');
      return res.json();
    },
    enabled: !!eventId
  });

  // Fetch stats
  const { data: stats = {} } = useQuery({
    queryKey: ['invite-stats', eventId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/events/${eventId}/invites/stats`, { headers });
      if (!res.ok) throw new Error('Failed to fetch stats');
      return res.json();
    },
    enabled: !!eventId
  });

  // Create single invite
  const createInviteMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/events/${eventId}/invites`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to create invite');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['event-invites']);
      queryClient.invalidateQueries(['invite-stats']);
      setShowAddDialog(false);
      toast.success('Invite created');
    },
    onError: (error) => toast.error(error.message)
  });

  // Send invite
  const sendInviteMutation = useMutation({
    mutationFn: async (inviteId) => {
      const res = await fetch(`${API_URL}/api/events/${eventId}/invites/${inviteId}/send`, {
        method: 'POST',
        headers
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to send invite');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['event-invites']);
      queryClient.invalidateQueries(['invite-stats']);
      toast.success('Invite email sent');
    },
    onError: (error) => toast.error(error.message)
  });

  // Send all pending
  const sendAllMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/events/${eventId}/invites/send-all`, {
        method: 'POST',
        headers
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to send invites');
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['event-invites']);
      queryClient.invalidateQueries(['invite-stats']);
      toast.success(`${data.count} invite emails queued`);
    },
    onError: (error) => toast.error(error.message)
  });

  // Delete invite
  const deleteMutation = useMutation({
    mutationFn: async (inviteId) => {
      const res = await fetch(`${API_URL}/api/events/${eventId}/invites/${inviteId}`, {
        method: 'DELETE',
        headers
      });
      if (!res.ok) throw new Error('Failed to delete');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['event-invites']);
      queryClient.invalidateQueries(['invite-stats']);
      toast.success('Invite deleted');
    },
    onError: (error) => toast.error(error.message)
  });

  // CSV Upload
  const uploadMutation = useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_URL}/api/events/${eventId}/invites/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (!res.ok) throw new Error('Upload failed');
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['event-invites']);
      queryClient.invalidateQueries(['invite-stats']);
      setShowUploadDialog(false);
      toast.success(`Created ${data.created} invites. ${data.skipped} skipped.`);
    },
    onError: (error) => toast.error(error.message)
  });

  // Filter invites
  const filteredInvites = invites.filter(inv =>
    inv.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inv.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inv.company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const statusBadge = (invite) => {
    const statusColors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'sent': 'bg-blue-100 text-blue-800',
      'opened': 'bg-purple-100 text-purple-800',
      'registered': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return (
      <Badge className={statusColors[invite.status] || 'bg-gray-100'}>
        {invite.status}
      </Badge>
    );
  };

  const toggleSelect = (inviteId) => {
    setSelectedInvites(prev => 
      prev.includes(inviteId) 
        ? prev.filter(id => id !== inviteId)
        : [...prev, inviteId]
    );
  };

  const selectAll = () => {
    if (selectedInvites.length === filteredInvites.length) {
      setSelectedInvites([]);
    } else {
      setSelectedInvites(filteredInvites.map(i => i.id));
    }
  };

  return (
    <div className="space-y-6" data-testid="pre-registration-invites">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold">{stats.total_invites || 0}</p>
            <p className="text-sm text-gray-500">Total Invites</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{stats.emails_sent || 0}</p>
            <p className="text-sm text-gray-500">Emails Sent</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-purple-600">{stats.emails_opened || 0}</p>
            <p className="text-sm text-gray-500">Opened</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{stats.registered || 0}</p>
            <p className="text-sm text-gray-500">Registered</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-orange-600">{stats.conversion_rate || 0}%</p>
            <p className="text-sm text-gray-500">Conversion</p>
          </CardContent>
        </Card>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex gap-2 items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search name, email, company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 w-64"
              data-testid="invite-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="opened">Opened</SelectItem>
              <SelectItem value="registered">Registered</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowUploadDialog(true)} data-testid="upload-csv-btn">
            <Upload className="h-4 w-4 mr-2" />
            Upload CSV
          </Button>
          <Button 
            variant="outline" 
            onClick={() => sendAllMutation.mutate()}
            disabled={sendAllMutation.isPending}
            data-testid="send-all-btn"
          >
            <Send className="h-4 w-4 mr-2" />
            Send All Pending
          </Button>
          <Button onClick={() => setShowAddDialog(true)} data-testid="add-invite-btn">
            <UserPlus className="h-4 w-4 mr-2" />
            Add Invite
          </Button>
        </div>
      </div>

      {/* Selected Actions */}
      {selectedInvites.length > 0 && (
        <div className="flex items-center gap-4 p-3 bg-blue-50 rounded-lg">
          <span className="text-sm font-medium">{selectedInvites.length} selected</span>
          <Button size="sm" variant="outline" onClick={() => setSelectedInvites([])}>
            Clear
          </Button>
          <Button size="sm" onClick={() => {
            // Bulk send selected
            selectedInvites.forEach(id => sendInviteMutation.mutate(id));
            setSelectedInvites([]);
          }}>
            <Send className="h-4 w-4 mr-1" />
            Send Selected
          </Button>
        </div>
      )}

      {/* Invites Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Loading invites...</div>
          ) : filteredInvites.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Mail className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No invites found</p>
              <p className="text-sm">Add invites manually or upload a CSV file</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <Checkbox 
                        checked={selectedInvites.length === filteredInvites.length}
                        onCheckedChange={selectAll}
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invitee</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email Activity</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredInvites.map((invite) => (
                    <tr key={invite.id} className="hover:bg-gray-50" data-testid={`invite-row-${invite.id}`}>
                      <td className="px-4 py-3">
                        <Checkbox 
                          checked={selectedInvites.includes(invite.id)}
                          onCheckedChange={() => toggleSelect(invite.id)}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium">{invite.name}</p>
                          <p className="text-sm text-gray-500">{invite.email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{invite.company || '-'}</td>
                      <td className="px-4 py-3">{statusBadge(invite)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 text-sm">
                          {invite.email_sent && (
                            <span className="flex items-center gap-1 text-blue-600">
                              <Send className="h-3 w-3" /> Sent
                            </span>
                          )}
                          {invite.email_opened && (
                            <span className="flex items-center gap-1 text-purple-600">
                              <Eye className="h-3 w-3" /> Opened
                            </span>
                          )}
                          {invite.status === 'registered' && (
                            <span className="flex items-center gap-1 text-green-600">
                              <UserCheck className="h-3 w-3" /> Registered
                            </span>
                          )}
                          {!invite.email_sent && invite.status === 'pending' && (
                            <span className="text-gray-400">Not sent</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex gap-2 justify-end">
                          {invite.status === 'pending' && !invite.email_sent && (
                            <Button 
                              size="sm"
                              onClick={() => sendInviteMutation.mutate(invite.id)}
                              disabled={sendInviteMutation.isPending}
                              data-testid={`send-invite-${invite.id}`}
                            >
                              <Send className="h-4 w-4 mr-1" />
                              Send
                            </Button>
                          )}
                          {invite.status === 'sent' && (
                            <Button 
                              size="sm"
                              variant="outline"
                              onClick={() => sendInviteMutation.mutate(invite.id)}
                              disabled={sendInviteMutation.isPending}
                            >
                              <RefreshCw className="h-4 w-4 mr-1" />
                              Resend
                            </Button>
                          )}
                          <Button 
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteMutation.mutate(invite.id)}
                            data-testid={`delete-invite-${invite.id}`}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Single Invite Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Invite</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const form = new FormData(e.target);
              createInviteMutation.mutate({
                email: form.get('email'),
                name: form.get('name'),
                company: form.get('company'),
                title: form.get('title'),
                custom_message: form.get('custom_message')
              });
            }}
            className="space-y-4"
          >
            <div>
              <label className="text-sm font-medium">Email *</label>
              <Input name="email" type="email" required placeholder="email@example.com" data-testid="input-invite-email" />
            </div>
            <div>
              <label className="text-sm font-medium">Name *</label>
              <Input name="name" required placeholder="Full name" data-testid="input-invite-name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Company</label>
                <Input name="company" placeholder="Company name" />
              </div>
              <div>
                <label className="text-sm font-medium">Title</label>
                <Input name="title" placeholder="Job title" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Personal Message (Optional)</label>
              <Textarea name="custom_message" placeholder="Add a personal note to the invite email..." rows={3} />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={createInviteMutation.isPending} data-testid="submit-invite-btn">
                {createInviteMutation.isPending ? 'Creating...' : 'Create Invite'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* CSV Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Upload Invites from CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p className="text-sm text-gray-600 mb-2">Upload a CSV file with columns:</p>
              <code className="text-xs bg-gray-100 px-2 py-1 rounded">email, name, company, title, phone, tags</code>
              <input
                type="file"
                accept=".csv"
                className="mt-4 w-full"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    uploadMutation.mutate(e.target.files[0]);
                  }
                }}
                data-testid="csv-file-input"
              />
            </div>
            <div className="text-xs text-gray-500">
              <p><strong>Example CSV:</strong></p>
              <pre className="bg-gray-50 p-2 rounded mt-1">
email,name,company,title
john@example.com,John Doe,Acme Inc,Manager
jane@example.com,Jane Smith,Tech Corp,Director
              </pre>
            </div>
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setShowUploadDialog(false)}>Close</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PreRegistrationInvites;
